import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '../../componentsv2/Typography';
import Button from '../../componentsv2/Button';
import Modal from '../../componentsv2/Modal';
import { useTranslation } from '../../hooks/i18n';
import { COLORS } from '../../themev2';
import { Nullable } from '../../types';
import { Company } from '../../types/companyUser';
import CloseIcon from '@mui/icons-material/Close';
import MessageSentIcon from '../../components/Icons/MessageSentIcon';
import { useMarkComplianceNotified, useComplianceInvite } from '../../hooks/useProductStatus';
import { isVerificationPending } from '../../utils/onboarding';
import { logEvent } from '@amplitude/analytics-browser';
import { complianceBannerEvents } from '../../constants/events';

interface ComplianceBannerProps {
  selectedCompany: Nullable<Company>;
}

const styles = {
  background: '#CEEDF8',
  iconColor: COLORS.indigo[500],
  buttonColor: 'secondary',
};

const ComplianceBanner = ({ selectedCompany }: ComplianceBannerProps) => {
  const { t } = useTranslation(['activation']);
  const [dismissed, setDismissed] = useState(false);
  const [isAdvisorModalOpen, setIsAdvisorModalOpen] = useState(false);

  const mustRender = (): boolean => {
    if (
      isVerificationPending(selectedCompany) ||
      !selectedCompany?.productStatus?.readyForBanking ||
      selectedCompany?.referralId !== null
    ) {
      return false;
    }
    if (selectedCompany?.country === 'us' && !selectedCompany?.productStatus?.complianceNotified) {
      if (
        !selectedCompany?.productStatus?.readyForCompliance ||
        !selectedCompany?.productStatus?.hasAccounting
      ) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    logEvent(complianceBannerEvents.COMPLIANCE_INVITE_SHOWN);
  }, []);

  const { mutate: markAdvisorNotified } = useMarkComplianceNotified({
    companyId: selectedCompany?.id ?? 0,
    productStatusId: selectedCompany?.productStatus?.id ?? 0,
  });

  const { mutate: sendComplianceNotification } = useComplianceInvite({
    companyId: selectedCompany?.id ?? 0,
    productStatusId: selectedCompany?.productStatus?.id ?? 0,
  });

  const handleCloseAdvisorModal = () => {
    setIsAdvisorModalOpen(false);
    markAdvisorNotified();
  };

  const handleAdvisorButtonClick = () => {
    logEvent(complianceBannerEvents.COMPLIANCE_INVITE_CTA_CLICKED);
    setDismissed(true);
    setIsAdvisorModalOpen(true);
    sendComplianceNotification();
  };

  const handleClose = () => {
    setDismissed(true);
    markAdvisorNotified();
    logEvent(complianceBannerEvents.COMPLIANCE_INVITE_DISMISSED);
  };

  if (!mustRender()) {
    return null;
  }

  return (
    <>
      {!dismissed && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              padding: '24px',
              alignItems: 'center',
              gap: '24px',
              alignSelf: 'stretch',
              borderRadius: '12px',
              background: styles.background,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  lineHeight: '20px',
                  color: COLORS.gray[1200],
                  mb: 1,
                }}
              >
                {t('application:dashboard.complianceBanner.title')}
              </Typography>
              <Typography
                sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '20px', color: COLORS.gray[1100] }}
              >
                {t('application:dashboard.complianceBanner.text')}
              </Typography>
            </Box>

            <Button onClick={handleAdvisorButtonClick} color="blue">
              {t('application:dashboard.complianceBanner.modal.talk')}
            </Button>

            <CloseIcon onClick={handleClose} style={{ marginLeft: 'auto', cursor: 'pointer' }} />
          </Box>
        </Grid>
      )}

      <Modal isOpen={isAdvisorModalOpen} handleClose={handleCloseAdvisorModal} isAutoHeight>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <MessageSentIcon />
        </Grid>

        <Grid item xs={12} sx={{ mb: 2 }}>
          <Typography
            sx={{ color: COLORS.brand.night, textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}
          >
            {t('application:dashboard.complianceBanner.modal.title')}
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ mb: 2 }}>
          <Typography sx={{ color: '#66758F', textAlign: 'center', fontSize: '16px' }} variant="body1">
            {t('application:dashboard.complianceBanner.modal.text')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Button fullWidth onClick={handleCloseAdvisorModal} color="secondary">
            {t('application:dashboard.complianceBanner.modal.close')}
          </Button>
        </Grid>
      </Modal>
    </>
  );
};

export default ComplianceBanner;
